import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  seaIceForecastFileStatusId,
  type SeaIceForecastFileStatusId,
} from './SeaIceForecastFileStatus.js'
import { userId, type UserId } from './User.js'
import { userApiKeyId, type UserApiKeyId } from './UserApiKey.js'

/** Identifier type for public.sea_ice_forecast_file_ */
export type SeaIceForecastFileId = string & { __brand: 'SeaIceForecastFileId' }

/** Represents the table public.sea_ice_forecast_file_ */
export default interface SeaIceForecastFileTable {
  /** Database type: pg_catalog.int8 */
  id: ColumnType<SeaIceForecastFileId, never, never>

  /** Database type: pg_catalog.timestamptz */
  receivedAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.text */
  originalFilename: ColumnType<string, string, string>

  /** Database type: pg_catalog.int4 */
  sizeBytes: ColumnType<number, number, number>

  /** Database type: pg_catalog.text */
  sha256: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  rawS3Key: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  status: ColumnType<
    SeaIceForecastFileStatusId,
    SeaIceForecastFileStatusId,
    SeaIceForecastFileStatusId
  >

  /** Database type: pg_catalog.date */
  dataDate: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.int2 */
  horizonGroup: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.text */
  displayS3Key: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int2 */
  normalizerVersion: ColumnType<number | null, number | null, number | null>

  /**
   * Database type: pg_catalog.jsonb
   * Array of {code, message}, set only on rejected rows. Untyped (unknown) in generated models; seaIceForecast.ts owns the shape.
   */
  rejectionReasons: ColumnType<unknown | null, unknown | null, unknown | null>

  /**
   * Database type: pg_catalog.jsonb
   * Array of {code, message}: non-fatal issues on an accepted row (may be empty). Untyped (unknown) in generated models; seaIceForecast.ts owns the shape.
   */
  warnings: ColumnType<unknown | null, unknown | null, unknown | null>

  /** Database type: pg_catalog.int8 */
  supersededBy: ColumnType<
    SeaIceForecastFileId | null,
    SeaIceForecastFileId | null,
    SeaIceForecastFileId | null
  >

  /** Database type: pg_catalog.uuid */
  uploadedBy: ColumnType<UserId, UserId, UserId>

  /** Database type: pg_catalog.uuid */
  apiKeyId: ColumnType<
    UserApiKeyId | null,
    UserApiKeyId | null,
    UserApiKeyId | null
  >

  /** Database type: pg_catalog.text */
  sourceIp: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  userAgent: ColumnType<string | null, string | null, string | null>
}

export type SeaIceForecastFile = Selectable<SeaIceForecastFileTable>

export type SeaIceForecastFileInitializer = Insertable<SeaIceForecastFileTable>

export type SeaIceForecastFileMutator = Updateable<SeaIceForecastFileTable>

export const seaIceForecastFileId =
  z.string() as unknown as z.Schema<SeaIceForecastFileId>

export const seaIceForecastFile = z.object({
  id: seaIceForecastFileId,
  receivedAt: z.coerce.date(),
  originalFilename: z.string(),
  sizeBytes: z.number(),
  sha256: z.string(),
  rawS3Key: z.string(),
  status: seaIceForecastFileStatusId,
  dataDate: z.coerce.date().nullable().optional(),
  horizonGroup: z.number().nullable().optional(),
  displayS3Key: z.string().nullable().optional(),
  normalizerVersion: z.number().nullable().optional(),
  rejectionReasons: z.unknown().nullable().optional(),
  warnings: z.unknown().nullable().optional(),
  supersededBy: seaIceForecastFileId.nullable().optional(),
  uploadedBy: userId,
  apiKeyId: userApiKeyId.nullable().optional(),
  sourceIp: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
}) as unknown as z.Schema<SeaIceForecastFile>

export const seaIceForecastFileInitializer = z.object({
  id: seaIceForecastFileId.optional(),
  receivedAt: z.coerce.date().optional(),
  originalFilename: z.string(),
  sizeBytes: z.number(),
  sha256: z.string(),
  rawS3Key: z.string(),
  status: seaIceForecastFileStatusId,
  dataDate: z.coerce.date().optional().nullable(),
  horizonGroup: z.number().optional().nullable(),
  displayS3Key: z.string().optional().nullable(),
  normalizerVersion: z.number().optional().nullable(),
  rejectionReasons: z.unknown().optional().nullable(),
  warnings: z.unknown().optional().nullable(),
  supersededBy: seaIceForecastFileId.optional().nullable(),
  uploadedBy: userId,
  apiKeyId: userApiKeyId.optional().nullable(),
  sourceIp: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
}) as unknown as z.Schema<SeaIceForecastFileInitializer>

export const seaIceForecastFileMutator = z.object({
  id: seaIceForecastFileId.optional(),
  receivedAt: z.coerce.date().optional(),
  originalFilename: z.string().optional(),
  sizeBytes: z.number().optional(),
  sha256: z.string().optional(),
  rawS3Key: z.string().optional(),
  status: seaIceForecastFileStatusId.optional(),
  dataDate: z.coerce.date().optional().nullable(),
  horizonGroup: z.number().optional().nullable(),
  displayS3Key: z.string().optional().nullable(),
  normalizerVersion: z.number().optional().nullable(),
  rejectionReasons: z.unknown().optional().nullable(),
  warnings: z.unknown().optional().nullable(),
  supersededBy: seaIceForecastFileId.optional().nullable(),
  uploadedBy: userId.optional(),
  apiKeyId: userApiKeyId.optional().nullable(),
  sourceIp: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
}) as unknown as z.Schema<SeaIceForecastFileMutator>
